import { getPanelsOnPage } from '../../../lib/comicPages'
import type { ComicPanelAspectPreset } from '../../../lib/comicProjectSettings'
import { pageHasReaderContent, pageLayoutLabel } from '../../../lib/comicReaderModel'
import type { ComicEpisode, ComicPage } from '../../../lib/comicTypes'
import { mediaDisplayUrl } from '../../../lib/resolvedMediaImage'
import { ComicPageLayoutGrid } from '../ComicPageLayoutGrid'
import { comicPanelRound } from '../comicPanelTokens'
import { ComicReaderEmptyPage } from './ComicReaderEmptyPage'

type Props = {
  angular: boolean
  episode: ComicEpisode
  page: ComicPage
  panelAspect: ComicPanelAspectPreset
  /** 翻页模式：整页居中、限制最大高度 */
  paginate?: boolean
  /** 滚动模式：整页图上下无缝衔接，无圆角/底色/额外套壳 */
  scroll?: boolean
  onSelectPage?: () => void
  onSelectPanel?: (panelId: string) => void
}

export function ComicReaderPageContent({
  angular,
  episode,
  page,
  panelAspect,
  paginate = false,
  scroll = false,
  onSelectPage,
  onSelectPanel,
}: Props) {
  const panels = getPanelsOnPage(episode, page)
  const whole = page.pageImageUrl?.trim() ? mediaDisplayUrl(page.pageImageUrl) : ''
  const hasContent = pageHasReaderContent(episode, page)

  if (!hasContent) {
    return (
      <ComicReaderEmptyPage
        angular={angular}
        pageTitle={page.title}
        layoutLabel={pageLayoutLabel(page.layout)}
        onEdit={onSelectPage}
      />
    )
  }

  const paginateFrame = paginate
    ? 'flex min-h-0 max-h-full w-full items-center justify-center overflow-hidden'
    : ''
  const paginateImg = paginate
    ? 'mx-auto block h-auto max-h-full w-auto max-w-full object-contain'
    : ''
  const scrollImg = scroll ? 'block h-auto w-full max-w-full align-top' : ''
  const defaultImg = !paginate && !scroll ? 'block w-full bg-surface-muted object-contain' : ''

  if (whole) {
    const img = (
      <img
        src={whole}
        alt={page.title}
        className={[paginateImg, scrollImg, defaultImg].filter(Boolean).join(' ')}
        draggable={false}
      />
    )
    const shellClass = scroll
      ? 'block w-full p-0'
      : [
          paginateFrame,
          comicPanelRound(angular),
          paginate ? '' : 'w-full bg-surface-muted',
        ]
          .filter(Boolean)
          .join(' ')
    if (onSelectPage) {
      return (
        <button
          type="button"
          className={[
            shellClass,
            'border-0 bg-transparent text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/50',
          ].join(' ')}
          onClick={onSelectPage}
        >
          {img}
        </button>
      )
    }
    return <div className={shellClass}>{img}</div>
  }

  const grid = (
    <ComicPageLayoutGrid
      angular={angular}
      layout={page.layout}
      panels={panels}
      panelAspect={panelAspect}
      compact
      onSelectPanel={
        onSelectPanel
          ? (panelId) => {
              onSelectPage?.()
              onSelectPanel(panelId)
            }
          : undefined
      }
    />
  )

  const gridShell = scroll
    ? 'block w-full px-3 py-4 sm:px-5'
    : [
        paginateFrame || 'block w-full',
        comicPanelRound(angular),
        scroll ? '' : 'bg-surface-muted/40',
        paginate ? 'scroll-region max-h-full min-h-0 overflow-auto overscroll-contain' : '',
      ]
        .filter(Boolean)
        .join(' ')

  if (onSelectPage) {
    return (
      <button
        type="button"
        className={[
          gridShell,
          'border-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50',
          scroll ? 'bg-transparent' : '',
        ].join(' ')}
        onClick={onSelectPage}
      >
        {grid}
      </button>
    )
  }

  return <div className={gridShell}>{grid}</div>
}
